import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DownloadSimple, FilePdf, FileDoc, File, MagnifyingGlass } from "@phosphor-icons/react";
import { api, API, shortDate } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CATEGORIES = ["all", "SOW", "determination_form", "budget", "salary_sheet", "procurement_backup",
  "report", "risk_assessment", "reimbursement", "amendment", "approval_memo", "closeout", "other"];

function iconFor(name = "") {
  const ext = name.split(".").pop().toLowerCase();
  if (ext === "pdf") return FilePdf;
  if (["doc", "docx"].includes(ext)) return FileDoc;
  return File;
}

export default function Documents() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    const params = {};
    if (category !== "all") params.category = category;
    const { data } = await api.get("/documents", { params });
    setDocs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const filtered = docs.filter((d) => !search || d.file_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader overline="Document Management" title="Documents">
        Centralized, categorized document store across all contracts and grants with metadata tagging.
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="doc-search" className="pl-9" placeholder="Search file name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="doc-cat-filter" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All categories" : c.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No documents" subtitle="Documents uploaded from contract or grant records will appear here." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d) => {
            const Icon = iconFor(d.file_name);
            return (
              <div key={d.id} data-testid={`docs-card-${d.id}`} className="bg-card border border-border rounded-md p-5">
                <div className="flex items-start gap-3">
                  <Icon size={28} className="text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{d.file_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{d.category.replace(/_/g, " ")} · {d.version}</div>
                    <div className="text-xs text-muted-foreground">{d.uploaded_by_name} · {shortDate(d.upload_date)}</div>
                  </div>
                </div>
                {d.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">{d.tags.map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>
                )}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                  <button className="text-xs text-primary hover:underline" onClick={() => navigate(`/${d.related_type}s/${d.related_id}`)}>
                    View {d.related_type}
                  </button>
                  <Button size="icon" variant="ghost" data-testid={`docs-download-${d.id}`} onClick={() => window.open(`${API}/documents/${d.id}/download`, "_blank")}>
                    <DownloadSimple size={16} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
